import {
  Menu,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { Eye } from 'lucide-react';
import { NOTIFICATION_TONE_CLASSES } from '../config/notification-tone';
import type { NotificationViewModel } from '../config/notification-presenters';

export type NotificationAction = 'open';

/**
 * Colunas da tela de notificações.
 *
 * As duas datas são colunas de primeira classe: DATA é quando o fato
 * aconteceu, LEITURA é quando o usuário viu. Sem leitura, `—`.
 */
export function createNotificationsColumns(
  onAction: (
    _action: NotificationAction,
    _notification: NotificationViewModel
  ) => void,
  typeLabels: Record<string, string>
): DataTableColumn<NotificationViewModel>[] {
  return [
    {
      accessorKey: 'type',
      header: 'TIPO',
      size: 170,
      cell: ({ row }) => {
        const notification = row.original;
        const tone = NOTIFICATION_TONE_CLASSES[notification.tone];

        return (
          <Tag.Root
            variant='outline'
            colorScheme={tone.tagColorScheme}
            size='sm'
          >
            {/* O rótulo vem do catálogo da API; o próprio código é o fallback
                para um tipo que o front ainda não conhece. */}
            <Tag.Label>
              {typeLabels[notification.type] ?? notification.type}
            </Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      accessorKey: 'title',
      header: 'NOTIFICAÇÃO',
      size: 380,
      cell: ({ row }) => {
        const notification = row.original;

        return (
          <div className='flex flex-col gap-1'>
            <span className='font-gotham font-bold text-alba-black-500'>
              {notification.title}
            </span>
            <span className='text-xs text-alba-gray-600'>
              {notification.description}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'DATA',
      size: 150,
      cell: ({ row }) => (
        <span className='text-sm text-alba-black-500'>
          {row.original.createdAtLabel}
        </span>
      ),
    },
    {
      accessorKey: 'readAt',
      header: 'LEITURA',
      size: 150,
      cell: ({ row }) => (
        <span className='text-sm text-alba-gray-700'>
          {row.original.readAtLabel ?? '—'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'STATUS',
      size: 110,
      sortable: false,
      cell: ({ row }) => {
        const isRead = row.original.read;

        return (
          <Tag.Root
            variant='outline'
            colorScheme={isRead ? 'gray' : 'blue'}
            size='sm'
          >
            <Tag.Label>{isRead ? 'Lida' : 'Não lida'}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      size: 50,
      sortable: false,
      cell: ({ row }) => (
        <div className='flex justify-end'>
          <Menu.Root>
            <Menu.Trigger />
            <Menu.Content>
              <Menu.Item
                value='open'
                icon={<Eye size={16} />}
                onSelect={() => onAction('open', row.original)}
              >
                Abrir
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        </div>
      ),
    },
  ];
}
